// A fullscreen photo lightbox shared by the public gallery and the manager Photos
// tab. Navigable by on-screen chevrons, keyboard arrows and touch swipe (wrapping
// at the ends), with an optional download. Context-free — labels are passed in —
// so it works both inside and outside the i18n provider.

import { useCallback, useEffect, useRef, type TouchEvent as ReactTouchEvent } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { downloadPhoto } from './downloadPhoto';

export interface LightboxPhoto {
  id: string;
  url: string;
  comment?: string;
}

export interface LightboxLabels {
  close: string;
  download: string;
  previous: string;
  next: string;
  /** Alt text for the image. */
  alt: string;
}

interface PhotoLightboxProps {
  photos: LightboxPhoto[];
  /** Index of the open photo, or null when the lightbox is closed. */
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  labels: LightboxLabels;
  /** Show a download button that saves the current photo. */
  showDownload?: boolean;
}

const SWIPE_THRESHOLD_PX = 40;

const EDGE_BUTTON_SX = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 2,
  color: '#fff',
  bgcolor: 'rgba(255,255,255,0.08)',
  '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' },
} as const;

export function PhotoLightbox({
  photos,
  index,
  onIndexChange,
  onClose,
  labels,
  showDownload = false,
}: PhotoLightboxProps) {
  const count = photos.length;
  const open = index !== null && index >= 0 && index < count;
  const photo = open ? photos[index] : null;

  const go = useCallback(
    (delta: number) => {
      if (index === null || count === 0) return;
      onIndexChange((index + delta + count) % count); // wrap around
    },
    [index, count, onIndexChange],
  );

  // Keyboard navigation (Escape is handled by the Dialog).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, go]);

  // Touch swipe: left → next, right → previous.
  const touchStartX = useRef<number | null>(null);
  const onTouchStart = (e: ReactTouchEvent) => {
    touchStartX.current = e.changedTouches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: ReactTouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = (e.changedTouches[0]?.clientX ?? touchStartX.current) - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) > SWIPE_THRESHOLD_PX) go(dx < 0 ? 1 : -1);
  };

  const hasNav = count > 1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      slotProps={{ paper: { sx: { bgcolor: 'rgba(8,6,10,0.97)' } } }}
    >
      {photo && (
        <Box
          sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <IconButton
            aria-label={labels.close}
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 2,
              color: '#fff',
              bgcolor: 'rgba(255,255,255,0.08)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' },
            }}
          >
            <CloseIcon />
          </IconButton>

          {hasNav && (
            <>
              <IconButton
                aria-label={labels.previous}
                onClick={() => go(-1)}
                sx={{ ...EDGE_BUTTON_SX, left: 8 }}
              >
                <ChevronLeftIcon fontSize="large" />
              </IconButton>
              <IconButton
                aria-label={labels.next}
                onClick={() => go(1)}
                sx={{ ...EDGE_BUTTON_SX, right: 8 }}
              >
                <ChevronRightIcon fontSize="large" />
              </IconButton>
            </>
          )}

          <Box sx={{ flexGrow: 1, minHeight: 0, display: 'grid', placeItems: 'center', p: { xs: 1, sm: 3 } }}>
            <Box
              key={photo.id}
              component="img"
              src={photo.url}
              alt={photo.comment || labels.alt}
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 1 }}
            />
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{
              p: 2,
              borderTop: '1px solid rgba(255,255,255,0.08)',
              backgroundColor: 'rgba(0,0,0,0.35)',
            }}
          >
            <Typography variant="body2" sx={{ flexGrow: 1, color: 'rgba(255,255,255,0.85)' }}>
              {photo.comment}
            </Typography>
            {hasNav && index !== null && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontVariantNumeric: 'tabular-nums' }}>
                {index + 1} / {count}
              </Typography>
            )}
            {showDownload && (
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => void downloadPhoto(photo.url, `${photo.id}.webp`)}
              >
                {labels.download}
              </Button>
            )}
          </Stack>
        </Box>
      )}
    </Dialog>
  );
}
